import { PaxCheckDocumentProvisionMethod } from "api/models";
import { StateCreator } from "zustand";

import AppContent from "../../content/content";
import { getParameterByName, initialLocale } from "../../utils";
import { SlicedStore } from "../store";

import ScannerError from "models/scanner-error";
import { ScannerInstanceType } from "models/scanner-type";
import { DocProcessingStatus } from "../../models/doc-processing-status";
import SessionIneligibilityReason from "models/session-ineligibility-reason";
import ApplyDocumentError from "services/data-provider/ApplyDocumentResponse";

export type ProcessingError =
  | ApplyDocumentError
  | "unknown_error"
  | "signals_mismatch"
  | "verification_required";

export interface AppSlice {
  isJourneyShown: boolean;
  showJourney: (isShown: boolean) => void;

  areContentAssetsLoaded: boolean;
  markContentAssetsLoaded: () => void;

  scannerError: ScannerError | null;
  setScannerError: (error: ScannerError) => void;

  capturerLoadedState: Map<string, boolean>;
  markCapturerLoaded: (type: ScannerInstanceType) => void;

  locale: string;
  changeLocale: (locale: string) => void;

  processedDocTypes: string[] | undefined;
  setProcessedDocTypes: (processedDocTypes: string[] | undefined) => void;

  provisionMethod: PaxCheckDocumentProvisionMethod;
  setProvisionMethod: (method: PaxCheckDocumentProvisionMethod) => void;

  returnUrl: string | null;
  setReturnUrl: (url: string | null) => void;

  chosenPaxSessionIds: string[] | null;
  setChosenPaxSessionIds: (paxSessionIds: string[] | null) => void;

  processingStatus: DocProcessingStatus;
  processingError: ProcessingError | null;
  setProcessingStatus: (
    status: DocProcessingStatus,
    error: ProcessingError | null
  ) => void;

  processingMayTakeLong: boolean | null;
  setProcessingMayTakeLong: (processingMayTakeLong: boolean | null) => void;

  processedFileName: string | null;
  setProcessedFileName: (fileName: string | null) => void;

  ineligibilityReason: SessionIneligibilityReason | null;
  setIneligibilityReason: (reason: SessionIneligibilityReason) => void;

  sessionIsLocked: boolean;
  setSessionIsLocked: (sessionIsLocked: boolean) => void;
}

export const createAppSlice: StateCreator<SlicedStore, [], [], AppSlice> = (
  set,
  get
) => ({
  isJourneyShown: true,
  showJourney: (isShown) => set(() => ({ isJourneyShown: isShown })),

  areContentAssetsLoaded: false,
  markContentAssetsLoaded: () => set(() => ({ areContentAssetsLoaded: true })),

  scannerError: null,
  setScannerError: (error: ScannerError) => {
    set(() => ({ scannerError: error }));
  },

  capturerLoadedState: new Map<string, boolean>([
    [ScannerInstanceType.REGULA, false],
  ]),
  markCapturerLoaded: (type: ScannerInstanceType) => {
    set((prev) => ({
      capturerLoadedState: new Map(prev.capturerLoadedState).set(type, true),
    }));
  },

  processedDocTypes: undefined,
  setProcessedDocTypes: (docTypes) =>
    set(() => ({ processedDocTypes: docTypes })),

  provisionMethod: PaxCheckDocumentProvisionMethod.SCAN,
  setProvisionMethod: (method) => set(() => ({ provisionMethod: method })),

  returnUrl: getParameterByName("return_url"),
  setReturnUrl: (url: string | null) => set(() => ({ returnUrl: url })),

  chosenPaxSessionIds: (() => {
    const raw = getParameterByName("chosen_pax_sessions");
    return raw
      ? raw
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : null;
  })(),
  setChosenPaxSessionIds: (ids: string[] | null) =>
    set(() => ({ chosenPaxSessionIds: ids })),

  processingStatus: DocProcessingStatus.None,
  processingError: null,
  setProcessingStatus: (status, error) =>
    set(() => ({ processingStatus: status, processingError: error })),

  processingMayTakeLong: null,
  setProcessingMayTakeLong: (processingMayTakeLong: boolean | null) =>
    set(() => ({ processingMayTakeLong: processingMayTakeLong })),

  processedFileName: null,
  setProcessedFileName: (file) => set(() => ({ processedFileName: file })),

  locale: initialLocale(),
  changeLocale: (locale: string) => {
    AppContent.shared.changeLocale(locale);
    set(() => ({ locale: locale }));
  },

  ineligibilityReason: null,
  setIneligibilityReason: (reason: SessionIneligibilityReason) =>
    set(() => ({ ineligibilityReason: reason })),

  sessionIsLocked: false,
  setSessionIsLocked: (sessionIsLocked: boolean) =>
    set(() => ({ sessionIsLocked: sessionIsLocked })),
});
